/**
 * @author NikolaV
 */

import { DocumentsImagesStoreAbstract } from '@shared/documents-images/DocumentsImagesStoreAbstract';
import { DocumentsImagesFilter } from '@shared/documents-images/ui/DocumentsImagesFilter';
import { DocumentsImagesPanelThumbnails } from '@shared/documents-images/ui/DocumentsImagesPanelThumbnails';
import { DocumentsPanelToolbar } from '@shared/documents-images/ui/DocumentsPanelToolbar';
import { ImagesPanelToolbar } from '@shared/documents-images/ui/ImagesPanelToolbar';
import { Events, EventsGlobal } from '@shared/Events';
import { FilterSelectionFeedback } from '@shared/map/FilterSelectionFeedback';
import { MapPanelFilter } from '@shared/map/MapPanelFilter';
import { TreeSelectionFeedback } from '@shared/map/TreeSelectionFeedback';
import { FileTreeService } from '@shared/services/FileTreeService';
import { childFilterPresets, Tree, TreeNode } from '@shared/ui-native/Tree';
import { Button } from '@shared/ui-react/Button';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { PageStateUtils } from '@shared/user-state/PageStateUtils';
import { cs } from '@shared/utils/utils-classname';
import { makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import './DocumentsImagesPanelGenericComponent.scss';
import { PdfTemplateFill } from '@shared/documents/ui/PdfTemplateFill';


@observer
export class DocumentsImagesPanelGenericComponent extends React.Component<{
    type: 'images' | 'documents'
    store: DocumentsImagesStoreAbstract<any>,
    treeData: ITreeData | null,
    storageKey: string,
    onOpenItem: (id: number) => void,
    onEditItem?: (id: number) => void,
    onEditItems?: (items: IDocument[] | IImage[]) => void,
    pinLeftOffset?: number,
    mobileView?: boolean,
    toggleExpandPanel?: () => void,
    onBulkEdit?: () => void,
    onOpenUploader?: () => void,
    onCreateExternalDocument?: () => void,
    showFillTab?: boolean,
    onOpenFillTemplate?: () => void,
    getRenamedName?: (template: string, description: string, folderUuid: string, latLon: [number, number], doc: IDocument) => string;
    getUserLatLon?: () => [number, number]
}> implements IStateful<IMapPageStateDocumentsImagesPanel> {
    public tree: Tree = null;
    private toolbarEl: HTMLDivElement = null;
    private selectionEl: HTMLDivElement = null;
    private treeEl: HTMLDivElement = null;

    @observable activeTab = 0;
    @observable isRendered = false;
    @observable isTwoColumns = false;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        if (this.props.treeData) {
            this.initTree();
        }

        setTimeout(() => {
            try {
                const ro = new ResizeObserver((entries, observer) => {
                    this.isTwoColumns = this.props.mobileView ? false : entries[0].contentRect.width > 550;
                });
                ro.observe(this.toolbarEl?.parentNode as HTMLDivElement);
            } catch { }
        }, 1000);
    }

    componentDidUpdate(prevProps: any) {
        if (!this.tree && prevProps.treeData !== this.props.treeData) {
            this.initTree();
        }
    }

    render() {
        const store = this.props.store;
        const contentMaxWidth = 500;
        const noItems = store.hasLoadedItems && store.items.length === 0;

        if (!this.props.treeData) return <div className='overlay-loading no-bg' style={{ height: '100%' }}></div>;

        let Thumbnails = null;
        if (this.props.store.thumbnails) {
            const folderName = FileTreeService.findNodeById(this.props.treeData, this.props.store.showThumbnailsForFolderId)?.name || '';
            const canDownload = kuura.hasPrivilege(this.props.type === 'images' ? 'Download_Image' : 'Download_Model');

            Thumbnails = <DocumentsImagesPanelThumbnails
                type={this.props.type}
                items={this.props.store.thumbnails?.filtered}
                unfilteredItems={this.props.store.thumbnails?.unfiltered}
                focusItemId={this.props.store.focusThumbnailId}
                folderName={folderName}
                canEdit={this.props.mobileView ? false : (this.props.type === 'images' ? kuura.hasPrivilege('IMAGE_PAGE') : true)}
                onEditSelected={this.props.onEditItems}
                onEdit={this.props.onEditItem}
                onClick={this.props.onOpenItem}
                onDelete={itemId => this.props.store.removeByIds([itemId])}
                onClose={this.closeThumbnailsPanel}
                onExpand={this.props.mobileView ? null : this.props.toggleExpandPanel}
                canDownload={canDownload && !this.props.mobileView}
            />;
        }

        return <>
            <div className="sidebar-toolbar" ref={el => this.toolbarEl = el}>
                {this.props.mobileView ? null : (this.props.type === 'documents' ?
                    <DocumentsPanelToolbar store={this.props.store} openCreateExternal={this.props.onCreateExternalDocument} openFillTemplate={this.props.onOpenFillTemplate} /> :
                    <ImagesPanelToolbar store={this.props.store} onBulkEdit={this.props.onBulkEdit} onOpenUploader={this.props.onOpenUploader} />
                )}
            </div>

            <div className={cs('doc-images-panel__wrapper', { 'two-col': this.isTwoColumns })}>
                <div className="flex-column of-h" style={{ flex: '1 1 50%' }}>
                    <Tabs displayValue="flex" flexDirection="column" index={this.props.mobileView ? this.activeTab : 0} onChange={i => this.activeTab = i}>
                        <TabPane title={__('general.folder_tree')}>
                            <div className="flex-column of-h" style={{ margin: '0 auto', width: '100%', maxWidth: this.props.mobileView ? contentMaxWidth : 'auto' }}>
                                <div className="flex-row gap-half ai-center">
                                    <div className='tree-toolbar-wrapper centered' style={{ display: noItems ? 'none' : 'block ', flexGrow: 1, overflow: 'hidden' }}></div>

                                    {(!this.props.mobileView && this.isRendered && !noItems) &&
                                        <MapPanelFilter
                                            pinned={store.filterPinned}
                                            pinLeftOffset={this.props.pinLeftOffset}
                                            opened={store.filterOpened}
                                            activeFiltersCount={store.numberOfAppliedFilters}
                                            clearFilter={store.clearFilter}
                                            togglePinned={() => {
                                                store.filterPinned = !store.filterPinned;
                                                store.filterOpened = true;
                                            }}
                                            toggleOpened={() => store.filterOpened = !store.filterOpened}>
                                            <DocumentsImagesFilter store={store} treeData={this.props.treeData} type={this.props.type} mobile={false} />
                                        </MapPanelFilter>
                                    }
                                </div>

                                <div ref={el => this.selectionEl = el} style={{ display: noItems ? 'none' : 'block ' }}></div>
                                <div ref={el => this.treeEl = el} style={{ display: noItems ? 'none' : 'block ', overflow: 'auto' }}></div>

                                {noItems &&
                                    <div className="sidebar-section-body-empty">
                                        <div className="panel-no-items flex-column gap-half">
                                            <i className={this.props.type === 'images' ? "iki iki-photos" : "iki iki-files"}></i>
                                            {this.props.type === 'images' ? __('js_images.no_images') : __('js_map.no_documents')}
                                        </div>
                                    </div>}
                            </div>
                        </TabPane>

                        {this.props.mobileView &&
                            <TabPane title={__('general.filters')} badgeColor={store.numberOfAppliedFilters > 0 ? 'green' : null}>
                                <div className="flex-column of-h" style={{ margin: '10px auto 0', maxWidth: contentMaxWidth }}>
                                    <FilterSelectionFeedback filtersCount={store.numberOfAppliedFilters} onClearFilters={store.clearFilter} showDefaultText={false} />
                                    <DocumentsImagesFilter store={store} treeData={this.props.treeData} type={this.props.type} mobile={true} />
                                    <div className="flex-row">
                                        <Button icon="iki iki-fit-to-screen" onClick={() => Events.trigger(this.props.type === 'images' ? EventsGlobal.MAP_FIT_PHOTOS : EventsGlobal.MAP_FIT_DOCUMENTS)}
                                            style={{ margin: '0px auto 20px' }} />
                                    </div>
                                </div>
                            </TabPane>
                        }

                        {(this.props.mobileView && this.props.store.thumbnails) &&
                            <TabPane title={<i className="iki iki-grid" />}>
                                {Thumbnails}
                            </TabPane>
                        }

                        {(this.props.showFillTab && this.props.type === 'documents') &&
                            <TabPane title={__('js_files.fill_template')}>
                                <PdfTemplateFill getRenamedName={this.props.getRenamedName} getUserLatLon={this.props.getUserLatLon} onSaved={() => this.props.store.fetchAndSet()} />
                            </TabPane>
                        }
                    </Tabs>
                </div>

                {!this.props.mobileView && this.props.store.thumbnails &&
                    <div className={cs('second-col', 'flex-column', 'of-h', { 'overlay-loading': this.props.store.isLoadingItems })}
                        style={{ flex: '1 1 50%', borderTop: '1px solid #ccc', paddingTop: 10, minHeight: 140 }}>
                        {Thumbnails}
                    </div>
                }
            </div>
        </>;
    }

    private initTree() {
        if (this.tree) return;

        this.tree = new Tree({
            rootEl: $(this.treeEl),
            showRoot: true,
            showCheckboxes: true,
            propagateCheckbox: false,
            selectionCheckbox: false,
            checkOnHighlightSelected: true,
            showContextMenuCreateFolder: this.props.type !== 'documents',
            childFilter: childFilterPresets.onlyFolders,
            hideEmptyFolders: true,
            hideEmptyFoldersAlways: this.props.type === 'documents' || this.props.mobileView,
            storageKey: this.props.storageKey,
            fetchContentCountsFn: this.props.store.getFolderCounts,
            toolbar: {
                show: true,
                elementSelector: '.tree-toolbar-wrapper'
            }
        });

        this.tree.on('render', () => {
            const store = this.props.store;

            setTimeout(() => {
                this.isRendered = true;
                this.renderFolderCounts()
                // refresh folder counts
                reaction(
                    () => store.counts,
                    () => this.renderFolderCounts(),
                    { delay: 100 }
                );

                if (this.stateful.queuedRestoreState) {
                    this.stateful.restoreState(this.stateful.queuedRestoreState);
                    this.stateful.queuedRestoreState = null;
                }
            })
        });

        this.tree.on('check', node => {
            this.applyFileTreeFilter();
        });

        this.tree.on('select', (e, data) => {
            const node: TreeNode = data.node;
            setTimeout(() => { // prevents double touch event, check ticket
                this.openThumbnailsPanel(node.id);
            }, this.props.mobileView ? 50 : 0);
        });

        this.tree.initAndRender(this.props.treeData);
    }

    renderFolderCounts = _.debounce(() => {
        if (this.tree) {
            this.tree.renderNodeCountsManual(this.props.store.counts.allFolderCounts, this.props.store.counts.filteredFolderCounts);
        }

        if (this.selectionEl) {
            ReactUtils.safeRender(<TreeSelectionFeedback
                checkedCount={this.props.store.filter.folderIds?.length ?? 0}
                loadedItemsCount={this.props.store.itemsFiltered.length}
                totalItemsCount={this.props.store.items.length}
                onClearSelection={this.clearSelection}
                onSelectAll={this.selectAll}
                countTooltip={__('js_map.images.selected_count_tooltip')}
            />, this.selectionEl);
        }
    }, 100);

    selectAll = () => {
        if (this.tree) {
            this.tree.setChecked(1, true, 'deep');
            this.applyFileTreeFilter();
            setTimeout(this.renderFolderCounts);
        }
    }

    clearSelection = () => {
        if (this.tree) {
            this.props.store.filter.folderIds = [];
            this.tree.setChecked(0, true, 'deep', true);
            this.applyFileTreeFilter();
        }
    }

    getSelected = (): TreeNode => {
        return this.tree?.getSelected();
    }

    getTreeNodeById = (id: number): TreeNode => {
        return this.tree?.getById(id);
    }

    applyFileTreeFilter = _.debounce(() => {
        let folderIds: number[] = [];
        if (this.tree) {
            folderIds = this.tree.getChecked().map(f => f.id);
        }
        this.props.store.setFilter({
            folderIds
        });
        this.tree.calculateCheckboxes(true);
    }, 100);

    openThumbnailsPanel(folderId: number, focusId?: number) {
        this.props.store.showThumbnailsForFolderId = folderId;
        if (focusId) {
            this.props.store.focusThumbnailId = focusId;
        }
        this.activeTab = 2;

        const folderNode = this.tree.getById(folderId);
        if (folderNode) {
            folderNode.setSelected(true, true, true);

            if (focusId) {
                folderNode.scrollTo();
            }
        }
    }

    showInPanel(idOrUuid: number | string) {
        const image = typeof idOrUuid === 'number' ? this.props.store.getById(idOrUuid) : this.props.store.getByUuid(idOrUuid);
        if (image) {
            this.openThumbnailsPanel(image.folderId, image.id);
        }
    }

    closeThumbnailsPanel = () => {
        this.props.store.showThumbnailsForFolderId = null;
        this.props.store.focusThumbnailId = null;
        this.activeTab = 0;

        const node = this.tree.getSelected();
        if (node) {
            node.setSelected(false, false, true);
        }
    }

    stateful = {
        queuedRestoreState: null,

        defaultState: {
            checkedFolderIds: [],
            uncheckedFolderIds: []
        },

        restoreDefaultState: (): void => {
            this.props.store.clearFilter();
            this.selectAll();
            this.stateful.restoreState(this.stateful.defaultState);
        },

        getCurrentState: (): IMapPageStateDocumentsImagesPanel => {
            if (!this.tree) return null;

            const { checkedIds, uncheckedIds } = this.tree?.getCheckedAndUncheckedFolderIds();

            return {
                checkedFolderIds: checkedIds,
                uncheckedFolderIds: uncheckedIds, // note, save even when all is unchecked (customer complaints)
                ...this.props.store.stateful.getCurrentState()
            };
        },

        restoreState: (state: IMapPageStateDocumentsImagesPanel): void => {
            if (!state) return null;

            if (!this.tree) {
                this.stateful.queuedRestoreState = JSON.parse(JSON.stringify(state)) as IMapPageStateDocumentsImagesPanel;
                return;
            }

            this.props.store.stateful.restoreState(state);

            if (PageStateUtils.stateHasKey<IMapPageStateDocumentsImagesPanel>(state, 'checkedFolderIds')) {
                const allFolderIds = new Set();
                this.tree.each(c => {
                    if (c.isFolder()) {
                        allFolderIds.add(c.id)
                    }
                }, true, true);

                state.checkedFolderIds = state.checkedFolderIds.filter(id => allFolderIds.has(id));
                this.props.store.filter.folderIds = state.checkedFolderIds;
                this.tree?.checkIds(state.checkedFolderIds, true, false);
            }

            this.stateful.queuedRestoreState = null;

            setTimeout(() => {
                this.applyFileTreeFilter();
            }, 50);
        }
    };
}
